/**
 * Single source of truth for application URLs.
 * Route definitions use the segments; links and redirects use the builders.
 */
export const paths = {
  home: '/',
  login: '/signin',
  password: '/signin/password',
  microsoft: '/signin/microsoft',
  forgotPassword: '/forgot-password',
  dashboard: '/overview',
  overview: '/overview',
  /** Trends: monthly series of every perspective (revenue, customers, acquisition, usage) and their signals. */
  trends: '/trends',
  revenue: '/revenue',
  subscriptions: '/subscriptions',
  /** Subscriptions dashboard; a static segment, so it takes precedence over the record route. */
  subscriptionsDashboard: '/subscriptions/dashboard',
  /** Subscription lifecycle analysis; a static segment like the dashboard. */
  subscriptionLifecycle: '/subscriptions/lifecycle',
  /** Churn analysis: rates, losses by month and group, survival, risk; a static segment like the dashboard. */
  subscriptionChurn: '/subscriptions/churn',
  /** Tiles of the subscription views with their counts; a static segment like the dashboard. */
  subscriptionViews: '/subscriptions/views',
  /** Two periods side by side (year to year, month to month…); a static segment like the dashboard. */
  subscriptionComparison: '/subscriptions/compare',
  /** Management reports of the subscriptions; a static segment like the dashboard. */
  subscriptionReports: '/subscriptions/reports',
  /** One management report, on the page and as a PDF, e.g. `/subscriptions/reports/executive-summary`. */
  subscriptionReport: (id: string) => `/subscriptions/reports/${id}`,
  renewals: '/renewals',
  offers: '/offers',
  /** Product offers subpages: Stripe prices by kind (standard, custom, archived), discounts, coupons. */
  offerPrices: (kind: 'standard' | 'custom' | 'archived') => `/offers/${kind}`,
  discounts: '/offers/discounts',
  coupons: '/offers/coupons',
  users: '/users',
  /** Tiles of the user views; a static segment, so it takes precedence over the record route. */
  userViews: '/users/views',
  apps: '/apps',
  /** Tiles of the app views; a static segment like the apps dashboard. */
  appViews: '/apps/views',
  /** Apps dashboard; a static segment, so it takes precedence over the app record route. */
  appsDashboard: '/apps/dashboard',
  cohorts: '/cohorts',
  totals: '/totals',
  /** Growth: checkout funnel, markets and the leads list. */
  conversion: '/conversion',
  /** What became of the trials: outcomes per start month, plans converted to, usage during trials. */
  trials: '/trials',
  markets: '/markets',
  leads: '/leads',
  /** Tiles of the lead views. */
  leadViews: '/leads/views',
  profile: '/profile',
  /** Record forms of the KPI portal lists. IDs are free text (user IDs contain ":" and "@"). */
  subscription: (id: string) => `/subscriptions/${encodeURIComponent(id)}`,
  user: (id: string) => `/users/${encodeURIComponent(id)}`,
  app: (id: string) => `/apps/${encodeURIComponent(id)}`,
  help: {
    root: '/help',
    /** Section page, e.g. `/help/kpis`. */
    section: (section: string) => `/help/${section}`,
    /** Topic page by section and topic ID, e.g. `/help/kpis/mrr`. */
    topic: (section: string, topic: string) => `/help/${section}/${topic}`,
    /** Metamodel views: `/help/metamodel` (system) or `/help/metamodel/data-model`. */
    metamodel: (view?: string) => (view ? `/help/metamodel/${view}` : '/help/metamodel'),
    /** Lineage of one node, e.g. `/help/metamodel/lineage/kpi/mrr`. */
    lineage: (kind: string, key: string) => `/help/metamodel/lineage/${kind}/${key}`,
  },
  analytics: {
    root: '/data',
    /** Tables and views of one database schema, e.g. `/data/stripe`. */
    schema: (schema: string) => `/data/${schema}`,
    /**
     * Overview of a source system of the menu's Data group (OAA, Stripe, BI), e.g.
     * `/data/source/oaa`. A static segment, so it takes precedence over a schema named "source".
     */
    source: (id: string) => `/data/source/${id}`,
    /**
     * The page of a table or view by its `{schema}/{slug}` key, e.g. `/data/stripe/charges`: totals,
     * then tabs for its overview, its records (Data), fields, relations, help and KPIs.
     */
    object: (key: string) => `/data/${key}`,
    /** The records of a table or view: its page on the Data tab. */
    records: (key: string) => `/data/${key}?tab=data`,
    /** Entity page. IDs are free text, so they are encoded. */
    record: (key: string, id: string) => `/data/${key}/${encodeURIComponent(id)}`,
  },
} as const;
